/**
 * Photo manager component - table of all countries with their photos and data
 */

import { useState, useMemo } from 'react';
import { Search, Filter, ImageIcon, Images, FileText, Check, X } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { CountryEditDialog } from './CountryEditDialog';
import { usePhotos } from '@/hooks/usePhotos';
import { useCountryData } from '@/hooks/useCountryData';
import { countries } from '@/data/countries';
import type { Country } from '@/data/countries/types';
import { cn } from '@/lib/utils';

type FilterType = 'all' | 'no-photos' | 'no-data' | 'complete';

export function PhotoManager() {
  const { photos, counts, loading: photosLoading, saveCountryPhotos } = usePhotos();
  const { data: countryData, loading: dataLoading, saveCountryData } = useCountryData();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loading = photosLoading || dataLoading;

  const filteredCountries = useMemo(() => {
    return countries.filter((country) => {
      const photoCount = counts[country.code] ?? 0;
      const hasData = !!countryData[country.code]?.description;

      // Search filter
      const matchesSearch =
        country.name.toLowerCase().includes(search.toLowerCase()) ||
        country.code.toLowerCase().includes(search.toLowerCase());

      if (!matchesSearch) return false;

      // Status filter
      switch (filter) {
        case 'no-photos':
          return photoCount === 0;
        case 'no-data':
          return !hasData;
        case 'complete':
          return photoCount > 0 && hasData;
        default:
          return true;
      }
    });
  }, [counts, countryData, search, filter]);

  const stats = useMemo(() => {
    let withPhotos = 0;
    let withData = 0;
    let complete = 0;
    let totalPhotos = 0;

    countries.forEach((country) => {
      const photoCount = counts[country.code] ?? 0;
      const hasData = !!countryData[country.code]?.description;

      totalPhotos += photoCount;
      if (photoCount > 0) withPhotos++;
      if (hasData) withData++;
      if (photoCount > 0 && hasData) complete++;
    });

    return { withPhotos, withData, complete, totalPhotos, total: countries.length };
  }, [counts, countryData]);

  const handleRowClick = (country: Country) => {
    setSelectedCountry(country);
    setDialogOpen(true);
  };

  const handleSavePhotos = async (
    photoList: Array<{
      photo_url: string;
      unsplash_id?: string;
      photographer?: string;
      photographer_url?: string;
    }>
  ) => {
    if (!selectedCountry) return;
    await saveCountryPhotos(selectedCountry.code, photoList);
  };

  const handleSaveData = async (data: {
    description?: string;
    capital?: string;
    population?: string;
    currency?: string;
    languages?: string;
    area?: string;
  }) => {
    if (!selectedCountry) return;
    await saveCountryData(selectedCountry.code, data);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Images className="w-5 h-5" />
                Country Manager
              </CardTitle>
              <CardDescription>
                {stats.complete}/{stats.total} complete ({stats.totalPhotos} photos, {stats.withData} with descriptions)
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search countries..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-48"
                />
              </div>
              <Select value={filter} onValueChange={(v) => setFilter(v as FilterType)}>
                <SelectTrigger className="w-44">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({stats.total})</SelectItem>
                  <SelectItem value="complete">Complete ({stats.complete})</SelectItem>
                  <SelectItem value="no-photos">No Photos ({stats.total - stats.withPhotos})</SelectItem>
                  <SelectItem value="no-data">No Description ({stats.total - stats.withData})</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          <ScrollArea className="h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">Flag</TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead className="w-24">Photos</TableHead>
                  <TableHead className="w-24">Data</TableHead>
                  <TableHead className="w-48">Preview</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Skeleton className="w-8 h-8 rounded" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="w-32 h-5" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="w-16 h-5" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="w-16 h-5" />
                      </TableCell>
                      <TableCell>
                        <Skeleton className="w-40 h-10 rounded" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : filteredCountries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                      No countries match your search
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCountries.map((country) => {
                    const countryPhotos = photos[country.code] ?? [];
                    const photoCount = counts[country.code] ?? 0;
                    const data = countryData[country.code];
                    const hasDescription = !!data?.description;

                    return (
                      <TableRow
                        key={country.code}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(country)}
                      >
                        <TableCell>
                          <span className="text-2xl">{country.flag}</span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{country.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {data?.capital || country.details?.capital || country.code}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          {photoCount > 0 ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-800">
                              <Check className="w-3 h-3 mr-1" />
                              {photoCount}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              <X className="w-3 h-3 mr-1" />
                              None
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {hasDescription ? (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                              <FileText className="w-3 h-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              <X className="w-3 h-3 mr-1" />
                              None
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {countryPhotos.length > 0 ? (
                            <div className="flex -space-x-2">
                              {countryPhotos.slice(0, 4).map((photo, idx) => (
                                <img
                                  key={photo.id}
                                  src={photo.photo_url}
                                  alt=""
                                  className={cn(
                                    'w-10 h-10 rounded object-cover border-2 border-background',
                                    idx > 0 && 'relative'
                                  )}
                                  style={{ zIndex: 10 - idx }}
                                />
                              ))}
                              {countryPhotos.length > 4 && (
                                <div
                                  className="w-10 h-10 rounded bg-muted border-2 border-background flex items-center justify-center text-xs font-medium"
                                  style={{ zIndex: 1 }}
                                >
                                  +{countryPhotos.length - 4}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-muted-foreground/50">
                              <ImageIcon className="w-4 h-4" />
                              <span className="text-sm">Click to add</span>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Country edit dialog */}
      {selectedCountry && (
        <CountryEditDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          country={selectedCountry}
          existingPhotos={photos[selectedCountry.code] ?? []}
          existingData={countryData[selectedCountry.code] ?? null}
          onSavePhotos={handleSavePhotos}
          onSaveData={handleSaveData}
        />
      )}
    </>
  );
}
